/**
 * Data Agent Sidebar
 * Right-side sliding panel with a chat interface to the Claude Agent SDK-powered
 * data analyst. Accessible from any page via a toggle in the sidebar/header.
 */

import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Send,
  Bot,
  User,
  Loader2,
  Wrench,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Sparkles,
  BarChart3,
  Users,
  FlaskConical,
  Activity,
  TrendingUp,
  Clock,
  Search,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AssistantMarkdown } from '@/components/shared/AssistantMarkdown';
import { cn } from '@/lib/utils';
import { streamDataAgentChat, type DataAgentSSEEvent, type DataAgentFilters } from '@/lib/data-agent-api';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { useEnvironment } from '@/lib/environment-context';
import { useDataAgentBridge } from '@/lib/data-agent-bridge-context';

// ---------------------------------------------------------------------------
// Skills — predefined queries grouped by category
// ---------------------------------------------------------------------------

interface Skill {
  label: string;
  query: string;
  icon: LucideIcon;
}

interface SkillCategory {
  name: string;
  icon: LucideIcon;
  skills: Skill[];
}

const SKILL_CATEGORIES: SkillCategory[] = [
  {
    name: 'Sessions',
    icon: BarChart3,
    skills: [
      { label: 'Session count', query: 'How many total sessions do we have?', icon: BarChart3 },
      { label: 'Session trends', query: 'Show session trends as a table', icon: TrendingUp },
      { label: 'Recent sessions', query: 'Show the 10 most recent sessions with user and timestamp', icon: Clock },
    ],
  },
  {
    name: 'Users',
    icon: Users,
    skills: [
      { label: 'Active users', query: 'How many active users? Show DAU and WAU', icon: Users },
      { label: 'Top users', query: 'Who are the top 10 most active users by session count?', icon: TrendingUp },
      { label: 'User growth', query: 'Show user signup trends', icon: Activity },
    ],
  },
  {
    name: 'QA & Testing',
    icon: FlaskConical,
    skills: [
      { label: 'Benchmarks', query: 'List all QA benchmark question lists with their question counts', icon: FlaskConical },
      { label: 'Test runs', query: 'Show the latest test runs with their status and scores', icon: Activity },
      { label: 'Quality scores', query: 'What are the average quality scores across recent test runs?', icon: BarChart3 },
    ],
  },
  {
    name: 'Insights',
    icon: Search,
    skills: [
      { label: 'Popular topics', query: 'What are users asking about most frequently?', icon: Search },
      { label: 'System health', query: 'Check the system health and report status of all services', icon: Activity },
    ],
  },
];

// Flat list of featured skills for the empty state (pick one from each category)
const FEATURED_SKILLS = [
  SKILL_CATEGORIES[0].skills[0], // Session count
  SKILL_CATEGORIES[1].skills[0], // Active users
  SKILL_CATEGORIES[1].skills[1], // Top users
  SKILL_CATEGORIES[2].skills[1], // Test runs
  SKILL_CATEGORIES[2].skills[2], // Quality scores
  SKILL_CATEGORIES[3].skills[0], // Popular topics
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  thinkingText: string;
  status?: 'streaming' | 'done' | 'error';
  toolCalls?: Array<{ tool: string; input: string }>;
  startedAt?: number;
}

interface DataAgentSidebarProps {
  width: number;
  onWidthChange: (width: number) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Drag Handle — thin interactive edge for resizing the panel
// ---------------------------------------------------------------------------

function DragHandle({ currentWidth, onDrag }: { currentWidth: number; onDrag: (width: number) => void }) {
  const handleMouseDown = (e: ReactMouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = currentWidth;

    const onMouseMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX; // dragging left = wider
      const newWidth = Math.max(360, Math.min(800, startWidth + delta));
      onDrag(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      className="w-1 hover:w-1.5 cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors shrink-0"
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DataAgentSidebar({ width, onWidthChange, onClose }: DataAgentSidebarProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Filter state
  const { env } = useEnvironment();
  const [dateRange, setDateRange] = useState<{ days?: number; from?: string; to?: string } | undefined>({ days: 30 });
  const bridge = useDataAgentBridge();
  const [excludeInternal, setExcludeInternalLocal] = useState(bridge.excludeInternal);

  // Bidirectional excludeInternal sync
  const setExcludeInternal = useCallback((value: boolean) => {
    setExcludeInternalLocal(value);
    bridge.setExcludeInternal(value);
  }, [bridge]);

  // Sync from bridge context (e.g. Product Insights toggled)
  useEffect(() => {
    setExcludeInternalLocal(bridge.excludeInternal);
  }, [bridge.excludeInternal]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus textarea on mount (panel is only rendered when open)
  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 200);
  }, []);

  // Consume pending bridge message on mount (panel was closed -> opens)
  const mountConsumedRef = useRef(false);
  useEffect(() => {
    if (mountConsumedRef.current) return;
    mountConsumedRef.current = true;
    const pending = bridge.consumePendingMessage();
    if (pending) {
      // Small delay to let handleSend stabilize after mount
      setTimeout(() => handleSendRef.current?.(pending), 100);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for drill-downs when sidebar is already open
  useEffect(() => {
    if (bridge.pendingMessage) {
      const msg = bridge.consumePendingMessage();
      if (msg) {
        if (isRunning) {
          setInput(msg);
        } else {
          handleSendRef.current?.(msg);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridge.pendingMessage]);

  const handleSend = useCallback((directMessage?: string) => {
    const trimmed = (directMessage ?? input).trim();
    if (!trimmed || isRunning) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed,
      thinkingText: '',
      status: 'done',
    };

    const assistantMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      thinkingText: '',
      status: 'streaming',
      toolCalls: [],
      startedAt: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setIsRunning(true);

    const assistantId = assistantMsg.id;

    const currentFilters: DataAgentFilters = { dateRange, excludeInternal, env };

    const controller = streamDataAgentChat(
      trimmed,
      (event: DataAgentSSEEvent) => {
        switch (event.type) {
          case 'thinking':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, thinkingText: m.thinkingText + event.text }
                  : m,
              ),
            );
            break;

          case 'text':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + event.text }
                  : m,
              ),
            );
            break;

          case 'tool_use':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      toolCalls: [
                        ...(m.toolCalls ?? []),
                        { tool: event.tool, input: event.input },
                      ],
                    }
                  : m,
              ),
            );
            break;

          case 'result':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content: event.text || m.content,
                      status: 'done',
                    }
                  : m,
              ),
            );
            break;

          case 'error':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content: m.content || event.error,
                      status: 'error',
                    }
                  : m,
              ),
            );
            break;

          case 'done':
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId && m.status === 'streaming'
                  ? { ...m, status: 'done' }
                  : m,
              ),
            );
            break;
        }
      },
      () => {
        setIsRunning(false);
        abortRef.current = null;
      },
      currentFilters,
    );

    abortRef.current = controller;
  }, [input, isRunning, dateRange, excludeInternal, env]);

  // Stable ref so effects can call handleSend without stale closures
  const handleSendRef = useRef(handleSend);
  useEffect(() => { handleSendRef.current = handleSend; }, [handleSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    setIsRunning(false);
  };

  // Inline panel — always rendered when mounted (parent controls visibility)
  return (
    <div className="flex h-screen sticky top-0 shrink-0" style={{ width }}>
      <DragHandle currentWidth={width} onDrag={onWidthChange} />
      <div className="flex-1 flex flex-col min-w-0 border-l border-border bg-background">
            {/* Header */}
            <div className="h-14 flex items-center justify-between px-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Data Agent</span>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Filter bar */}
            <div className="flex items-center gap-3 px-4 py-2 border-b border-border shrink-0">
              <DateRangeFilter
                value={dateRange}
                onChange={setDateRange}
                placeholder="All time"
                className="h-7 text-xs w-[160px]"
                showCustomRange={false}
              />
              <div className="flex items-center gap-1.5">
                <Switch
                  checked={excludeInternal}
                  onCheckedChange={setExcludeInternal}
                  className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 data-[state=checked]:[&>span]:translate-x-3"
                />
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">Excl. internal</span>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1" ref={scrollRef as any}>
              <div className="p-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center text-muted-foreground text-sm py-8 space-y-4">
                    <Bot className="h-10 w-10 mx-auto text-muted-foreground/40" />
                    <p className="font-medium">Ask me about your data</p>
                    <div className="flex flex-wrap gap-2 justify-center px-2">
                      {FEATURED_SKILLS.map((skill) => {
                        const Icon = skill.icon;
                        return (
                          <button
                            key={skill.label}
                            type="button"
                            onClick={() => handleSend(skill.query)}
                            disabled={isRunning}
                            className={cn(
                              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs',
                              'border border-border bg-background hover:bg-muted hover:border-primary/30',
                              'transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
                              'text-foreground/80 hover:text-foreground',
                            )}
                          >
                            <Icon className="h-3 w-3 text-muted-foreground" />
                            {skill.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}
              </div>
            </ScrollArea>

            {/* Composer */}
            <div className="border-t border-border p-3 shrink-0 space-y-2">
              <div className="flex gap-2">
                <SkillPicker onSelect={(query) => handleSend(query)} disabled={isRunning} />
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about your data... (Shift+Enter for new line)"
                  disabled={isRunning}
                  rows={2}
                  className={cn(
                    'flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm',
                    'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                    'disabled:opacity-50',
                  )}
                />
                {isRunning ? (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCancel}
                    className="shrink-0 self-end"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    onClick={() => handleSend()}
                    disabled={!input.trim()}
                    className="shrink-0 self-end"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thinking Indicator — Claude-style single animated line
// ---------------------------------------------------------------------------

const THINKING_LABELS = ['Thinking...', 'Analyzing...', 'Querying...'];

function ThinkingIndicator({ message }: { message: ChatMessage }) {
  const isStreaming = message.status === 'streaming';
  const toolCalls = message.toolCalls ?? [];
  const stepCount = toolCalls.length;
  const hasThinking = message.thinkingText.length > 0 || stepCount > 0;

  // Cycle through labels when no tool calls yet
  const [labelIdx, setLabelIdx] = useState(0);
  useEffect(() => {
    if (!isStreaming || stepCount > 0) return;
    const interval = setInterval(() => {
      setLabelIdx((prev) => (prev + 1) % THINKING_LABELS.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isStreaming, stepCount]);

  // Elapsed time
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!message.startedAt) return;
    if (!isStreaming) {
      setElapsed(Math.round((Date.now() - message.startedAt) / 1000));
      return;
    }
    const interval = setInterval(() => {
      setElapsed(Math.round((Date.now() - message.startedAt!) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isStreaming, message.startedAt]);

  // Collapsed state for the done summary
  const [isOpen, setIsOpen] = useState(false);

  // Nothing to show
  if (!isStreaming && !hasThinking) return null;

  // --- Streaming: single animated indicator ---
  if (isStreaming) {
    const latestTool = stepCount > 0 ? toolCalls[stepCount - 1].tool : null;
    const label = latestTool ? `Running ${latestTool}...` : THINKING_LABELS[labelIdx];

    return (
      <div className="flex items-center justify-between text-[11px] text-muted-foreground py-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/60 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary/80" />
          </span>
          <AnimatePresence mode="wait">
            <motion.span
              key={label}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
              className="truncate"
            >
              {label}
            </motion.span>
          </AnimatePresence>
        </div>
        {stepCount > 0 && (
          <span className="text-muted-foreground/50 shrink-0 ml-2">(step {stepCount})</span>
        )}
      </div>
    );
  }

  // --- Done: collapsed summary toggle ---
  const summaryText = `${stepCount} ${stepCount === 1 ? 'step' : 'steps'}${elapsed > 0 ? ` \u00b7 ${elapsed}s` : ''}`;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors py-1 group"
          type="button"
        >
          {isOpen ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          <span>{summaryText}</span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 space-y-2">
          {/* Thinking text */}
          {message.thinkingText && (
            <div className="text-[11px] italic text-muted-foreground/70 bg-muted/30 rounded px-2 py-1.5 max-h-40 overflow-y-auto whitespace-pre-wrap break-words">
              {message.thinkingText}
            </div>
          )}
          {/* Tool calls */}
          {stepCount > 0 && (
            <div className="space-y-1 ml-1 border-l-2 border-border pl-2">
              {toolCalls.map((tc, i) => (
                <ToolCallRow key={i} toolCall={tc} />
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ToolCallRow({ toolCall }: { toolCall: { tool: string; input: string } }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors py-0.5 w-full text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <Wrench className="h-3 w-3 shrink-0" />
        <span className="font-mono truncate">{toolCall.tool}</span>
        {expanded ? (
          <ChevronDown className="h-2.5 w-2.5 ml-auto shrink-0" />
        ) : (
          <ChevronRight className="h-2.5 w-2.5 ml-auto shrink-0" />
        )}
      </button>
      {expanded && toolCall.input && (
        <pre className="text-[10px] bg-muted/50 rounded p-2 mt-0.5 overflow-x-auto font-mono text-muted-foreground whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
          {toolCall.input}
        </pre>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skill Picker — persistent popover next to the input
// ---------------------------------------------------------------------------

function SkillPicker({
  onSelect,
  disabled,
}: {
  onSelect: (query: string) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 self-end text-muted-foreground hover:text-primary"
          disabled={disabled}
          title="Quick skills"
        >
          <Sparkles className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-72 p-0 max-h-80 overflow-y-auto"
        sideOffset={8}
      >
        <div className="p-2 border-b border-border">
          <p className="text-xs font-medium text-muted-foreground">Quick Skills</p>
        </div>
        <div className="p-1">
          {SKILL_CATEGORIES.map((cat) => {
            const CatIcon = cat.icon;
            return (
              <div key={cat.name} className="mb-1 last:mb-0">
                <div className="flex items-center gap-1.5 px-2 py-1">
                  <CatIcon className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                    {cat.name}
                  </span>
                </div>
                {cat.skills.map((skill) => {
                  const SkillIcon = skill.icon;
                  return (
                    <button
                      key={skill.label}
                      type="button"
                      className={cn(
                        'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-left',
                        'hover:bg-muted transition-colors cursor-pointer',
                        'text-foreground/80 hover:text-foreground',
                      )}
                      onClick={() => {
                        setOpen(false);
                        onSelect(skill.query);
                      }}
                    >
                      <SkillIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span>{skill.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Markdown renderer
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Message Bubble
// ---------------------------------------------------------------------------

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-2.5', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5',
          isUser
            ? 'bg-primary/10 text-primary'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {isUser ? (
          <User className="h-3.5 w-3.5" />
        ) : (
          <Bot className="h-3.5 w-3.5" />
        )}
      </div>

      {/* Content */}
      <div
        className={cn(
          'flex-1 min-w-0 space-y-1',
          isUser && 'text-right',
        )}
      >
        {/* Thinking indicator */}
        {!isUser && <ThinkingIndicator message={message} />}

        {/* Message text */}
        <div
          className={cn(
            'inline-block rounded-lg px-3 py-2 text-sm',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground',
            message.status === 'error' && 'border border-error/30',
          )}
        >
          {message.status === 'error' && (
            <div className="flex items-center gap-1 text-error text-xs mb-1">
              <AlertCircle className="h-3 w-3" />
              <span>Error</span>
            </div>
          )}

          {message.content ? (
            isUser ? (
              <div className="whitespace-pre-wrap break-words">
                {message.content}
              </div>
            ) : (
              <div className="break-words max-w-full overflow-hidden">
                <AssistantMarkdown content={message.content} />
              </div>
            )
          ) : message.status === 'streaming' ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="text-xs">Thinking...</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
